import React from 'react';
import { Link } from 'react-router-dom';
import { Check, X, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { FadeUp, StaggerContainer, StaggerItem, ScaleIn } from '../components/ScrollReveal';

const VANILLA_IMG = 'https://images.pexels.com/photos/14381803/pexels-photo-14381803.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
const BEANS_IMG = 'https://customer-assets.emergentagent.com/job_bb23729c-0dea-4683-8280-e0aa97a36711/artifacts/rpuw479q_IMG_2753.jpeg';
const serif = { fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic', fontWeight: 400 };

const WhyDifferent = () => {
  const { lang } = useLanguage();
  const t = translations[lang].whyDifferent;
  const common = translations[lang].common;

  // Suppression de la rubrique "Relations Directes avec les Agriculteurs" (d1)
  const differentiators = ['d2', 'd3', 'd4', 'd5', 'd6'];
  const icons = ['✅', '🔍', '⭐', '✈️', '💬'];

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-end pb-16 overflow-hidden" data-testid="why-hero">
        <div className="absolute inset-0 z-0">
          <img src={VANILLA_IMG} alt="Premium vanilla" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#2C2416]/55" />
        </div>
        <div className="relative z-10 container-max pt-28 text-center w-full">
          <FadeUp>
            <p className="section-overline">{t.overline}</p>
            <h1 className="text-5xl md:text-7xl text-white leading-tight mb-4" style={serif}>
              {t.heroTitle}<br /><em className="not-italic" style={{ color: '#E8D5A0' }}>{t.heroTitleAccent}</em>
            </h1>
            <div className="w-10 h-px bg-[#C9A84C] mx-auto mt-4" />
          </FadeUp>
        </div>
      </section>

      {/* DIFFERENTIATORS */}
      <section className="section-padding bg-[#FAF7F2]" data-testid="why-diff">
        <div className="container-max">
          <FadeUp>
            <h2 className="text-4xl text-center text-[#2C2416] mb-4" style={serif}>{t.diffTitle}</h2>
            <div className="gold-divider mx-auto" />
            <p className="text-[#7A6E63] text-center max-w-xl mx-auto mt-4 mb-12 text-sm leading-relaxed">{t.diffDesc}</p>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {differentiators.map((key, i) => (
              <FadeUp key={key} delay={i * 0.07}>
                <div className="bg-white border border-[#E5DDD2] rounded-xl p-7 hover:border-[#C9A84C]/40 hover:shadow-sm transition-all group h-full" data-testid={`diff-${key}`}>
                  <div className="text-2xl mb-4">{icons[i]}</div>
                  <h3 className="text-base font-semibold text-[#2C2416] mb-2">{t[`${key}Title`]}</h3>
                  <p className="text-[#7A6E63] text-sm leading-relaxed">{t[`${key}Desc`]}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON (dark) */}
      <section className="section-padding bg-[#2B2016]" data-testid="why-comparison">
        <div className="container-max">
          <FadeUp>
            <h2 className="text-4xl text-center text-white mb-12" style={serif}>{t.comparisonTitle}</h2>
          </FadeUp>
          <ScaleIn delay={0.1} className="overflow-x-auto">
            <table className="w-full" data-testid="comparison-table">
              <thead>
                <tr>
                  <th className="p-4 text-left text-white/30 text-[10px] uppercase tracking-widest border-b border-white/10 w-1/3">{t.compLabel}</th>
                  <th className="p-4 text-center border-b border-[#C9A84C]/20 w-1/3">
                    <span className="text-[#C9A84C]" style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: '1.1rem' }}>VANELYS</span>
                  </th>
                  <th className="p-4 text-center border-b border-white/10 w-1/3">
                    <span className="text-white/40 text-sm">{t.compStandard}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {t.rows.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white/3' : ''}>
                    <td className="p-4 text-white/60 text-sm border-b border-white/5">{row.label}</td>
                    <td className="p-4 border-b border-[#C9A84C]/10 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Check size={13} className="text-[#C9A84C]" />
                        <span className="text-white/80 text-sm">{row.v}</span>
                      </div>
                    </td>
                    <td className="p-4 border-b border-white/5 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <X size={13} className="text-white/25" />
                        <span className="text-white/35 text-sm">{row.s}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScaleIn>
        </div>
      </section>

      {/* CTA split */}
      <section className="section-padding bg-[#F0EBE0]" data-testid="why-cta">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScaleIn>
              <img src={BEANS_IMG} alt="VANELYS vanilla" className="rounded-2xl w-full h-[360px] object-cover" />
            </ScaleIn>
            <FadeUp delay={0.2}>
              <h2 className="text-4xl text-[#2C2416] mb-5" style={serif}>
                {lang === 'fr' ? 'Prêt à Découvrir la Différence ?' : 'Ready to Experience the Difference?'}
              </h2>
              <div className="gold-divider" />
              <p className="text-[#7A6E63] text-sm leading-relaxed mt-3 mb-8">
                {lang === 'fr'
                  ? "Rejoignez nos clients qui font confiance à VANELYS pour leur approvisionnement en vanille premium."
                  : "Join our clients who trust VANELYS for their premium vanilla supply."}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/quote" className="btn-primary">{common.requestQuote} <ArrowRight size={13} /></Link>
                <Link to="/contact" className="text-[#8B4513] hover:text-[#2C2416] text-xs uppercase tracking-widest font-medium transition-colors flex items-center gap-1">
                  {common.contactUs}
                </Link>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyDifferent;